import { IndexObjectType } from 'components/dataGrid/BasicDataGrid';
import { NONE_FILTER_LIST } from 'consts/const';
import { StepCommandType } from 'containers/Project/ProjectData/sideBar/steps';
import _ from 'lodash';
import { useProjectAPI } from 'services/project/project.api';
import useAttachStore from 'stores/project/data/preprocess/useAttachStore';
import usePreprocessStore from 'stores/project/data/preprocess/usePreprocessStore';
import useStepsStore from 'stores/project/data/steps/useStepsStore';
import useGraphStore from 'stores/project/data/useGraphStore';
import { ColumnInfoType } from 'stores/project/data/useTableDataStore';
import useProjectDataStore from 'stores/useProjectDataStore';
import { DropDownListOptionType } from 'types/common/CommonType';
import { Util } from 'utils';

export const useAddStep = () => {
  const {
    currentStepOrderNum,
    currentAttachStepOrderNum,
    isAttachStepMode,
    defaultStepData,
    stepsList,
    selectedAppendList,
    delStepsList,
    addStepsList,
    setCurrentStepId,
    setCurrentStepOrderNum,
    setCurrentAttachStepOrderNum,
    addAttachStepsList,
    setSelectedAppendList,
  } = useStepsStore();
  const { projectData } = useProjectDataStore();
  const { getColumnInfo, getColumnInfoList } = useProjectAPI();
  const { leftColumnList, setNumberOfAttach } = useAttachStore();
  const { currentFilterList } = useGraphStore();
  const { setPreprocessMode, setPreprocessTargetColumnList } = usePreprocessStore();

  const handleSelectedAppendList = (
    libraryDetailId: string,
    stepId?: string,
    orderNum?: number,
    columnList?: DropDownListOptionType[]
  ) => {
    let newAppendList = [...selectedAppendList];

    if (newAppendList.some((item) => item.libraryDetailId === libraryDetailId)) {
      newAppendList = newAppendList.filter((item) => item.libraryDetailId !== libraryDetailId);
    } else {
      newAppendList.push({
        libraryDetailId,
        stepId: stepId || undefined,
        orderNum: orderNum || undefined,
        leftColumnList: columnList || [],
      });
    }

    setSelectedAppendList(newAppendList);
  };

  const countAppendSteps = (): number => {
    const allStepList = _.cloneDeep(stepsList);
    let count = 0;

    for (let step of allStepList) {
      if (step.command === 'append') count = ++count;
      else continue;
    }
    setNumberOfAttach(count);
    return count;
  };

  const deleteStepData = async (stepId: string) => {
    let stepList = stepsList;
    const step = stepList.find((step) => step.stepId === stepId);

    if (step) {
      delStepsList(step.orderNum);
    }
  };

  const addAppendSteps = async (libraryDetailIds: string[], type: 'delete' | 'add') => {
    if (type === 'delete') {
      for (let libraryDetailId of libraryDetailIds) {
        deleteStepData(
          selectedAppendList.find((item) => item.libraryDetailId === libraryDetailId)?.stepId
        );
      }

      setSelectedAppendList([]);
    } else {
      const stepFilterList = NONE_FILTER_LIST.includes('append') ? [] : currentFilterList;

      let localAppendList = [...selectedAppendList];
      let localStepCount = stepsList.filter((s) => s.command === 'append').length;

      try {
        const response = await getColumnInfoList(libraryDetailIds);

        if (!response?.isSuccess || !response.resultObject) throw new Error();

        const columnInfoList = response.resultObject as Record<string, ColumnInfoType[]>;

        for (let libraryDetailId of libraryDetailIds) {
          const lastItem = localAppendList.at(-1);
          const nextOrderNum = lastItem ? lastItem.orderNum + 1 : currentStepOrderNum + 1;

          const stepId = Util.createUUID();

          const columnList = columnInfoList[libraryDetailId];

          if (!columnList) continue;

          const leftColumns = lastItem
            ? lastItem.leftColumnList.map((list: any) => list.label)
            : leftColumnList.map((list) => list.label);
          const rightColumns = columnList.map((list) => list.columnName);

          let leftColumn: IndexObjectType = {};
          let rightColumn: IndexObjectType = {};
          let matchedColumn: IndexObjectType = {};

          for (let left of leftColumns) {
            if (rightColumns.includes(left)) {
              matchedColumn = {
                ...matchedColumn,
                [left]: '',
              };
              continue;
            }
            leftColumn = { ...leftColumn, [left]: '' };
          }

          for (let right of rightColumns) {
            if (leftColumns.includes(right)) {
              matchedColumn[right] = right;
              continue;
            }
            rightColumn = { ...rightColumn, [right]: '' };
          }

          addStepsList({
            ...defaultStepData,
            command: 'append',
            stepSortList: [],
            orderNum: nextOrderNum,
            stepFilterList,
            stepId: stepId,
            append: {
              attachStepList: [{ ...defaultStepData, command: 'start' }],
              attachSortList: [],
              attachFilterList: [],
              numberOfAppend: localStepCount,
              currentLibraryDetailId: projectData.libraryDetailId,
              targetLibraryDetailId: libraryDetailId,
              matchInfo: {
                matchedColumns: matchedColumn,
                unmatchedColumnsInDf1: leftColumn,
                unmatchedColumnsInDf2: rightColumn,
              },
            },
          });

          localAppendList.push({
            libraryDetailId,
            stepId,
            orderNum: nextOrderNum,
            leftColumnList: [
              ...leftColumns.map((item: string) => ({ label: item, value: item })),
              ...rightColumns.map((item: string) => ({ label: item, value: item })),
            ].filter(
              (item, index, self) => self.findIndex((v) => v.value === item.value) === index
            ),
          });
          localStepCount++;
        }

        setSelectedAppendList(localAppendList);
      } catch (error) {
        console.error(error);
      }
    }
  };

  const addStep = async (
    mode: StepCommandType,
    columnName?: string,
    chatReq?: string,
    libraryDetailId?: string,
    multipleAppend?: boolean
  ) => {
    // const previousOrderNum = hasSkippedStepOrder();
    // if (
    //   currentInitialStep?.orderNum === previousOrderNum &&
    //   currentInitialStep?.command === mode
    // ) {
    //   return;
    // }

    const originColumnName = columnName
      ? columnName.replaceAll('$$_ORIGIN_REPLACE_', '').replaceAll('$$_ORIGIN_FILL_', '')
      : '';

    let replaceColumnName = originColumnName;

    if (mode === 'fill' && replaceColumnName) {
      replaceColumnName = `$$_ORIGIN_FILL_${replaceColumnName}`;
    }
    if (mode === 'replace' && replaceColumnName) {
      replaceColumnName = `$$_ORIGIN_REPLACE_${replaceColumnName}`;
    }

    let nextOrderNum;
    const stepFilterList = NONE_FILTER_LIST.includes(mode || '') ? [] : currentFilterList;
    const stepId = Util.createUUID();

    if (isAttachStepMode) {
      nextOrderNum = currentAttachStepOrderNum + 1;
      addAttachStepsList(
        {
          ...defaultStepData,
          command: mode,
          stepSortList: [],
          orderNum: nextOrderNum,
          stepFilterList,
          stepId: stepId,
          ...(chatReq
            ? {
                defaultStep: {
                  valueObject: chatReq,
                  columnNameList: [],
                },
              }
            : {}),
        },
        true
      );
      setPreprocessMode(mode);
      if (originColumnName) {
        setPreprocessTargetColumnList([replaceColumnName]);
      }
      setCurrentAttachStepOrderNum(nextOrderNum);
    } else if (mode === 'append' && libraryDetailId && multipleAppend) {
      if (selectedAppendList.some((item) => item.libraryDetailId === libraryDetailId)) {
        deleteStepData(
          selectedAppendList.find((item) => item.libraryDetailId === libraryDetailId)?.stepId
        );

        handleSelectedAppendList(libraryDetailId);
      } else {
        nextOrderNum = selectedAppendList[selectedAppendList.length - 1]
          ? selectedAppendList[selectedAppendList.length - 1].orderNum + 1
          : currentStepOrderNum + 1;

        const response = await getColumnInfo(libraryDetailId);
        const columnList = response?.resultList;

        const leftColumns = selectedAppendList[selectedAppendList.length - 1]
          ? selectedAppendList[selectedAppendList.length - 1].leftColumnList.map(
              (list: any) => list.label
            )
          : leftColumnList.map((list) => list.label);
        const rightColumns = columnList.map((list) => list.columnName);

        let leftColumn: IndexObjectType = {};
        let rightColumn: IndexObjectType = {};
        let matchedColumn: IndexObjectType = {};

        for (let left of leftColumns) {
          if (rightColumns.includes(left)) {
            matchedColumn = {
              ...matchedColumn,
              [left]: '',
            };
            continue;
          }
          leftColumn = { ...leftColumn, [left]: '' };
        }

        for (let right of rightColumns) {
          if (leftColumns.includes(right)) {
            matchedColumn[right] = right;
            continue;
          }
          rightColumn = { ...rightColumn, [right]: '' };
        }

        addStepsList({
          ...defaultStepData,
          command: mode,
          stepSortList: [],
          orderNum: nextOrderNum,
          stepFilterList,
          stepId: stepId,
          append: {
            attachStepList: [{ ...defaultStepData, command: 'start' }],
            attachSortList: [],
            attachFilterList: [],
            numberOfAppend: countAppendSteps(),
            currentLibraryDetailId: projectData.libraryDetailId,
            targetLibraryDetailId: libraryDetailId,
            matchInfo: {
              matchedColumns: matchedColumn,
              unmatchedColumnsInDf1: leftColumn,
              unmatchedColumnsInDf2: rightColumn,
            },
          },
        });

        if (originColumnName) {
          setPreprocessTargetColumnList([replaceColumnName]);
        }

        handleSelectedAppendList(
          libraryDetailId,
          stepId,
          nextOrderNum,
          [
            ...leftColumns.map((item: string) => ({ label: item, value: item })),
            ...rightColumns.map((item: string) => ({ label: item, value: item })),
          ].filter((item, index, self) => self.findIndex((v) => v.value === item.value) === index)
        );
      }
    } else {
      nextOrderNum = currentStepOrderNum + 1;
      addStepsList(
        {
          ...defaultStepData,
          command: mode,
          stepSortList: [],
          orderNum: nextOrderNum,
          stepFilterList,
          stepId: stepId,
          ...(chatReq
            ? {
                defaultStep: {
                  valueObject: chatReq,
                  columnNameList: [],
                },
              }
            : {}),
        },
        true
      );
      setPreprocessMode(mode);
      if (originColumnName) {
        setPreprocessTargetColumnList([replaceColumnName]);
      }
      setCurrentStepOrderNum(nextOrderNum);
    }

    setCurrentStepId(stepId);
  };

  return { addStep, addAppendSteps };
};
